import { Fragment } from "react";
import { AttributeTypes, CustomAttributeTypeFieldTemplateTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUIContentCustomAttributeField, AXNUIContentForm, AXNUILayout, CustomAttributeFieldCustomJSON, SelectOption } from "@iddataweb/idw-common-ui/types";
import { isStringTrue, getAXNUIAttributeLabel, getCustomAttributeFieldCustomizationData } from "@iddataweb/idw-common-ui/utils";
import { AxnAttributeTypeField, AxnAttributeTypeFieldCustomization } from "@idw-types/data/reference-data/axn";
import { AttributeTypeFieldsMap } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { AXNUIThemingAsyncInputComponent } from "../containers/common/axnui-theming-preview-container/axnui-theming-async-input-component/axnui-theming-async-input-component";
import { AXNUIThemingSelectInputComponent } from "../containers/common/axnui-theming-preview-container/axnui-theming-select-input-component/axnui-theming-select-input-component";
import { AXNUIThemingTextInputComponent } from "../containers/common/axnui-theming-preview-container/axnui-theming-text-input-component/axnui-theming-text-input-component";
import { AttributeClassificationTypes } from "@enums/attribute-classification-types";
import { AXNUIThemingRadioInputComponent } from "@containers/common/axnui-theming-preview-container/axnui-theming-radio-input-component/axnui-theming-radio-input-component";
import { AXNUIThemingDateInputComponent } from "@containers/common/axnui-theming-preview-container/axnui-theming-date-input-component/axnui-theming-date-input-component";
import { AXNUIThemingTelephoneInputComponent } from "@containers/common/axnui-theming-preview-container/axnui-theming-telephone-input-component/axnui-theming-telephone-input-component";
import countryRegionData from "country-region-data";
import { US_STATE_SELECT_OPTIONS } from "@iddataweb/idw-common-ui/constants";

const injectOptionalText = (label: string, isRequired: boolean, optionalMessage: string) => {
    return `${label} ${isRequired ? "" : `${optionalMessage}`}`;
};

export const getCountryOptions = (shouldShowLabelWithCountryCode: boolean): Array<SelectOption> => {
    return countryRegionData.map(country => (
        {
            label: shouldShowLabelWithCountryCode ? `${country.countryName} (${country.countryShortCode})` : country.countryName,
            value: country.countryShortCode.toUpperCase()
        }));
}

const getDocumentTypeOptions = (content: AXNUIContent, type: AttributeTypes): Array<SelectOption> => {
    switch (type) {
        case AttributeTypes.LICENSE_OR_ID_CARD:
            return [
                {
                    label: content.form.pickerInput.driverLicenseOptionLabel,
                    value: 'license',
                },
                {
                    label: content.form.pickerInput.idCardOptionLabel,
                    value: 'idcard',
                }
            ];
        case AttributeTypes.LICENSE_OR_PASSPORT:
        case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
        case AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT:
            return [
                {
                    label: content.form.pickerInput.driverLicenseOptionLabel,
                    value: 'license',
                },
                {
                    label: content.form.pickerInput.passportOptionLabel,
                    value: 'passport',
                }
            ];
        case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
            return [
                {
                    label: content.form.pickerInput.driverLicenseOptionLabel,
                    value: 'license',
                },
                {
                    label: content.form.pickerInput.passportOptionLabel,
                    value: 'passport',
                },
                {
                    label: content.form.pickerInput.idCardOptionLabel,
                    value: 'idcard',
                }
            ];
        case AttributeTypes.PASSPORT_OR_ID_CARD:
            return [
                {
                    label: content.form.pickerInput.passportOptionLabel,
                    value: 'passport',
                },
                {
                    label: content.form.pickerInput.idCardOptionLabel,
                    value: 'idcard',
                }
            ];
        case AttributeTypes.AADHAR_PAN_OR_PASSPORT:
            return [
                {
                    label: content.form.pickerInput.aadharCardOptionLabel,
                    value: 'aadhar',
                },
                {
                    label: content.form.pickerInput.panCardOptionLabel,
                    value: 'pan',
                },
                {
                    label: content.form.pickerInput.passportOptionLabel,
                    value: 'passport',
                }
            ];
        default:
            return [];
    }
};

const getPreferencePinOptions = (attributeTypeName: AttributeTypes, pinPreferenceInput: {
    sms: string;
    smsText: string;
    voice: string;
    voiceText: string;
    email: string;
    emailText: string;
    pinVerified: string;
}): Array<(SelectOption<string> & { label2: string; })> => {
    if (attributeTypeName === AttributeTypes.PIN_DELIVERY_PREFERENCE) {
        return [
            {
                label: pinPreferenceInput.sms,
                label2: pinPreferenceInput.smsText,
                value: ""
            },
            {
                label: pinPreferenceInput.voice,
                label2: pinPreferenceInput.voiceText,
                value: ""
            }
        ]
    }

    if (attributeTypeName === AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE) {
        return [
            {
                label: pinPreferenceInput.sms,
                label2: pinPreferenceInput.smsText,
                value: ""
            },
            {
                label: pinPreferenceInput.voice,
                label2: pinPreferenceInput.voiceText,
                value: ""
            },
            {
                label: pinPreferenceInput.email,
                label2: pinPreferenceInput.emailText,
                value: ""
            }
        ];
    }

    return [];
}

const getAttributeSelectOptions = (
    attribute: FormattedRpServiceConfigurationAttribute,
    axnuiContent: AXNUIContent
) => {
    let options: Array<SelectOption & { label2?: string }> = [];

    if (
        [
            AttributeTypes.NATIONALITY,
            AttributeTypes.COUNTRY
        ].includes(attribute.attributeType.name)
    ) {
        options = getCountryOptions(attribute.attributeType.name === AttributeTypes.NATIONALITY);
    } else if (
        [
            AttributeTypes.PIN_DELIVERY_PREFERENCE,
            AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE
        ].includes(attribute.attributeType.name)
    ) {
        options = getPreferencePinOptions(attribute.attributeType.name, axnuiContent.form.pinPreferenceInput);
    } else if (
        [
            AttributeTypes.LICENSE_OR_ID_CARD,
            AttributeTypes.LICENSE_OR_PASSPORT,
            AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT,
            AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE,
            AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD,
            AttributeTypes.PASSPORT_OR_ID_CARD,
            AttributeTypes.AADHAR_PAN_OR_PASSPORT
        ].includes(attribute.attributeType.name)
    ) {
        options = getDocumentTypeOptions(axnuiContent, attribute.attributeType.name);
    } else if (attribute.attributeType.name === AttributeTypes.GENDER) {
        options = [
            { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' }
        ];
    } else if (attribute.attributeType.name === AttributeTypes.BIO_GOV_OR_KBA) {
        options = [
            { label: axnuiContent.form.pickerInput.kbaOptionLabel, value: 'kba' },
            { label: axnuiContent.form.pickerInput.bioGovOptionLabel, value: 'biogov' }
        ];
    } else if (attribute.attributeType.name === AttributeTypes.DRIVERS_LICENSE_STATE) {
        options = US_STATE_SELECT_OPTIONS;
    }

    return options;
};

export const getAttributePlaceHolderFromContent = (attributeType: AttributeTypes, axnuiContent: AXNUIContent) => {
    const validationContent = axnuiContent.validation;

    switch (attributeType) {
        case AttributeTypes.FULL_NAME: {
            return validationContent.fullName.required;
        }
        case AttributeTypes.COMPANY_NAME:
            return validationContent.companyName.required;
        case AttributeTypes.INTERNATIONAL_TELEPHONE:
            return validationContent.internationalTelephone.required;
        case AttributeTypes.PIN_DELIVERY_PREFERENCE:
            return validationContent.pinDeliveryPreference.required;
        case AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE:
            return validationContent.pinDeliveryPreference.required;
        case AttributeTypes.EMAIL:
            return validationContent.email.required;
        case AttributeTypes.DOB:
            return validationContent.dateOfBirth.required;
        case AttributeTypes.NATIONAL_ID:
            return validationContent.nationalid.required;
        case AttributeTypes.DATE:
            return validationContent.date.required;
        case AttributeTypes.GENDER:
            return validationContent.gender.required;
        case AttributeTypes.COUNTRY:
            return validationContent.country.required;
        case AttributeTypes.LICENSE_OR_ID_CARD:
        case AttributeTypes.LICENSE_OR_PASSPORT:
        case AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT:
        case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
        case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
        case AttributeTypes.PASSPORT_OR_ID_CARD:
            return validationContent.idSelection.required;
        case AttributeTypes.DRIVERS_LICENSE_STATE:
            return validationContent.driversLicenseState.required;
        case AttributeTypes.BIO_GOV_OR_KBA:
            return validationContent.bioGovOrKbaSelection.required;
        case AttributeTypes.NATIONALITY:
            return validationContent.nationality.required;
        case AttributeTypes.ALPHANUMERIC:
        case AttributeTypes.ALPHANUMERIC_OTHER:
        case AttributeTypes.NUMERIC:
        default:
            return '';
    }
};

export const getComponentForAttribute = (
    attribute: FormattedRpServiceConfigurationAttribute,
    axnuiContent: AXNUIContent,
    axnuiLayout: AXNUILayout,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>,
    localizeStrings: boolean,
    showAfterCapturePreview: boolean,
    selectedLanguage: LocaleTypes
) => {
    const isRequired = !isStringTrue(attribute.optionalFlag);

    if (attribute.attributeClassification.name === AttributeClassificationTypes.CUSTOM) {
        return getCustomComponentForAttribute(
            attribute,
            axnuiContent,
            axnuiLayout,
            axnAttributeTypeFields,
            axnAttributeTypeFieldCustomizations,
            isRequired,
            selectedLanguage
        )
    } else {
        const attributeLabel = getAXNUIAttributeLabel(
            localizeStrings,
            axnuiContent.labels.attributes[attribute.attributeType?.name],
            attribute.attributeLabel?.label
        );
        const label = injectOptionalText(attributeLabel, isRequired, axnuiContent.form.genericInput.optional);

        const placeholder = getAttributePlaceHolderFromContent(
            attribute.attributeType.name,
            axnuiContent
        ) || `Please enter a ${label}`;

        const options = getAttributeSelectOptions(attribute, axnuiContent);

        switch (attribute.attributeType.name) {
            case AttributeTypes.FULL_NAME:
            case AttributeTypes.COMPANY_NAME:
            case AttributeTypes.EMAIL:
            case AttributeTypes.INTERNATIONAL_ADDRESS:
            case AttributeTypes.SSN9:
            case AttributeTypes.SSN4:
            case AttributeTypes.NATIONAL_ID:
            case AttributeTypes.DRIVERS_LICENSE_NUMBER:
            case AttributeTypes.ID_CARD_NUMBER:
            case AttributeTypes.PASSPORT_NUMBER:
            case AttributeTypes.AADHAR_CARD_NUMBER:
            case AttributeTypes.PAN_NUMBER:
            case AttributeTypes.PHYSICIAN_LICENSE_NUMBER:
            case AttributeTypes.POLICY_NUMBER:
            case AttributeTypes.ALPHANUMERIC:
            case AttributeTypes.ALPHANUMERIC_OTHER:
            case AttributeTypes.NUMERIC:
                return (
                    <AXNUIThemingTextInputComponent
                        attribute={attribute}
                        isRequired={isRequired}
                        label={label}
                        placeholder={placeholder}
                    />
                );
            case AttributeTypes.GENDER:
            case AttributeTypes.COUNTRY:
            case AttributeTypes.LICENSE_OR_ID_CARD:
            case AttributeTypes.LICENSE_OR_PASSPORT:
            case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
            case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
            case AttributeTypes.PASSPORT_OR_ID_CARD:
            case AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT:
            case AttributeTypes.AADHAR_PAN_OR_PASSPORT:
            case AttributeTypes.DRIVERS_LICENSE_STATE:
            case AttributeTypes.BIO_GOV_OR_KBA:
            case AttributeTypes.NATIONALITY:
                return (
                    <AXNUIThemingSelectInputComponent
                        attribute={attribute}
                        isRequired={isRequired}
                        label={label}
                        placeholder={placeholder}
                        options={options}
                    />
                );
            case AttributeTypes.DRIVERS_LICENSE_IMAGES:
            case AttributeTypes.CAMERA_SNAPSHOT:
            case AttributeTypes.PASSPORT_IMAGE:
            case AttributeTypes.AADHAR_CARD_IMAGES:
            case AttributeTypes.AUDIT_TRAIL_IMAGE:
            case AttributeTypes.LOW_QUALITY_AUDIT_TRAIL_IMAGE:
            case AttributeTypes.ID_CARD_IMAGES:
            case AttributeTypes.PAN_IMAGES:
                return (
                    <AXNUIThemingAsyncInputComponent
                        attribute={attribute}
                        isRequired={isRequired}
                        label={label}
                        showAfterCapturePreview={showAfterCapturePreview}
                        axnuiContent={axnuiContent}
                    />
                );

            case AttributeTypes.DATE:
            case AttributeTypes.DOB:
                return (
                    <AXNUIThemingDateInputComponent
                        id={attribute.attributeType.id}
                        isRequired={isRequired}
                        label={label}
                        locale={selectedLanguage}
                    />
                );
            case AttributeTypes.INTERNATIONAL_TELEPHONE:
                return (
                    <AXNUIThemingTelephoneInputComponent
                        id={attribute.attributeType.id}
                        isRequired={isRequired}
                        label={label}
                    />
                );
            default:
                return null;
        }
    }
};

export const getCustomComponentForAttribute = (
    attribute: FormattedRpServiceConfigurationAttribute,
    axnuiContent: AXNUIContent,
    axnuiLayout: AXNUILayout,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>,
    isRequired: boolean,
    selectedLanguage: LocaleTypes): React.ReactNode => {

    const attrTypeFields = axnAttributeTypeFields.filter(atf =>
        atf.attributeTypeID === attribute.attributeType.id
    );

    const customAttributeElements: Array<React.ReactNode> = attrTypeFields.map(field => {
        const customAttributeFieldCustomJSON = getCustomAttributeCustomJSON(
            attribute,
            field,
            axnuiContent,
            axnuiLayout,
            axnAttributeTypeFieldCustomizations,
            selectedLanguage
        );

        if (customAttributeFieldCustomJSON) {
            return getCustomAttributeInputByTemplateName(
                attribute,
                field.templateName,
                axnuiContent.form.genericInput.optional,
                customAttributeFieldCustomJSON,
                isRequired);
        }

        return null;

    });

    return (
        <>
            {customAttributeElements.map((element, index) => (
                <Fragment key={`custom-attr-element-${index}`}>
                    {element}
                </Fragment>
            ))}
        </>
    );
};

export const getCustomAttributeCustomJSON = (
    attribute: FormattedRpServiceConfigurationAttribute,
    field: AxnAttributeTypeField,
    axnuiContent: AXNUIContent,
    axnuiLayout: AXNUILayout,
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>,
    selectedLanguage: LocaleTypes
) => {
    const customization = axnAttributeTypeFieldCustomizations.find(custom => custom.attributeTypeFieldID === field.id);
    if (customization) {
        try {
            const parsedJSON = JSON.parse(customization.customJSON) as CustomAttributeFieldCustomJSON;
            return getCustomAttributeFieldCustomizationData(
                attribute.attributeType.id,
                field.name,
                parsedJSON,
                axnuiContent,
                axnuiLayout,
                selectedLanguage
            );
        } catch (error) {
            console.error(`Error parsing custom json for attribute: ${attribute.name} (${attribute.attributeType.id}) - attribute field: ${field.name} (${field.id}). Error: ${error}`);
        }
    }

    return null;
}

export const getCustomAttributeInputByTemplateName = (
    attribute: FormattedRpServiceConfigurationAttribute,
    templateName: CustomAttributeTypeFieldTemplateTypes,
    axnuiOptionalMessage: string,
    customAttributeFieldCustomJSON: {
        options: Array<SelectOption>;
        content: AXNUIContentCustomAttributeField;
    },
    isAttributeRequired: boolean
): React.ReactNode => {

    const label = injectOptionalText(customAttributeFieldCustomJSON.content.label, isAttributeRequired, axnuiOptionalMessage);
    const placeholder = customAttributeFieldCustomJSON.content.placeholder;

    switch (templateName) {
        case CustomAttributeTypeFieldTemplateTypes.CUSTOM_NUMERIC:
        case CustomAttributeTypeFieldTemplateTypes.CUSTOM_ALPHA_NUMERIC:
            return (
                <AXNUIThemingTextInputComponent
                    attribute={attribute}
                    isRequired={isAttributeRequired}
                    label={label}
                    placeholder={placeholder}
                />
            );
        case CustomAttributeTypeFieldTemplateTypes.CUSTOM_DROPDOWN:
            return (
                <AXNUIThemingSelectInputComponent
                    attribute={attribute}
                    isRequired={isAttributeRequired}
                    label={label}
                    placeholder={placeholder}
                    options={customAttributeFieldCustomJSON.options}
                />
            );
        case CustomAttributeTypeFieldTemplateTypes.CUSTOM_RADIO_BUTTON:
            return (
                <AXNUIThemingRadioInputComponent
                    attribute={attribute}
                    isRequired={isAttributeRequired}
                    label={label}
                    options={customAttributeFieldCustomJSON.options}
                />
            );
        default:
            return null;
    }
}

export const getAttributeFieldLabel = (axnAttributeTypeFields: Array<AxnAttributeTypeField>, placeholderKey: string) => {
    return axnAttributeTypeFields.find(attrField => {
        return placeholderKey.includes(attrField.name);
    })?.label;
}

export const getAXNUIFormContentKey = (attributeTypeName: AttributeTypes, key: string, value: string, axnuiContentForm: AXNUIContentForm): AXNUIContentForm => {
    switch (attributeTypeName) {
        case AttributeTypes.FULL_NAME: {
            return {
                ...axnuiContentForm,
                fullNameCaptureInput: {
                    ...axnuiContentForm?.fullNameCaptureInput,
                    [key]: value
                }
            }
        }

        case AttributeTypes.INTERNATIONAL_ADDRESS: {
            return {
                ...axnuiContentForm,
                internationalAddress: {
                    ...axnuiContentForm?.internationalAddress,
                    placeholders: {
                        ...axnuiContentForm?.internationalAddress?.placeholders,
                        [key]: value
                    }
                }
            }
        }

        default: {
            return null;
        }


    }
}


export const getAttributeFieldPlaceholder = (attributeTypeName: AttributeTypes, attributeTypeID: string, axnuiContentForm: AXNUIContentForm, axnAttributeTypeFieldMap: AttributeTypeFieldsMap) => {
    const fields: Array<any> = [];

    switch (attributeTypeName) {
        case AttributeTypes.FULL_NAME: {
            fields.push(
                {
                    key: "fnamePlaceholder",
                    value: axnuiContentForm.fullNameCaptureInput.fnamePlaceholder,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "fnamePlaceholder")
                }
            );
            fields.push(
                {
                    key: "mnamePlaceholder",
                    value: axnuiContentForm.fullNameCaptureInput.mnamePlaceholder,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "mnamePlaceholder")
                }
            );
            fields.push(
                {
                    key: "lnamePlaceholder",
                    value: axnuiContentForm.fullNameCaptureInput.lnamePlaceholder,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "lnamePlaceholder")
                }
            );
            fields.push(
                {
                    key: "suffixPlaceholder",
                    value: axnuiContentForm.fullNameCaptureInput.suffixPlaceholder,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "suffixPlaceholder")
                }
            );
            break;
        }

        case AttributeTypes.INTERNATIONAL_ADDRESS: {
            fields.push(
                {
                    key: "street_number",
                    value: axnuiContentForm.internationalAddress.placeholders.street_number,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "street_number") || "street_number"
                }
            );

            fields.push(
                {
                    key: "route",
                    value: axnuiContentForm.internationalAddress.placeholders.route,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "route") || "route"
                }
            );

            fields.push(
                {
                    key: "premise",
                    value: axnuiContentForm.internationalAddress.placeholders.premise,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "subpremise") || "premise"
                }
            );

            fields.push(
                {
                    key: "locality",
                    value: axnuiContentForm.internationalAddress.placeholders.locality,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "locality") || "locality"
                }
            );

            fields.push(
                {
                    key: "postal_code",
                    value: axnuiContentForm.internationalAddress.placeholders.postal_code,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "postal_code") || "postal_code"
                }
            );

            fields.push(
                {
                    key: "administrative_area_level_1",
                    value: axnuiContentForm.internationalAddress.placeholders.administrative_area_level_1,
                    label: getAttributeFieldLabel(axnAttributeTypeFieldMap[attributeTypeID], "administrative_area_level_1") || "administrative_area_level_1"
                }
            );
            break;
        }
    }

    return fields;

};